import { z } from 'zod'
import { ackEnvelope, assertAck, flatCountEnvelope } from './envelope'
import type { ApiClient } from './client'

/**
 * Platform users — the people who can sign in, administered across
 * organizations. `/lc/mgmt/org/*` and `/lc/mgmt/users/*`.
 *
 * NOT the same list as `users.ts`, which is `/{clusterId}/api/users/list` —
 * the SCOPE directory used to pick a case assignee. Two user lists, two
 * endpoints, two row shapes, and only one of them is administration.
 *
 * Contract read off the legacy service and screens (`services/userlist.js`,
 * `pages/admin/Users/list`, `pages/admin/Organisations/userAddEditModal`).
 * The live sweep is NO help here: `gotham/admin/user/list` captured only
 * `/lc/users/access-details` and `tenant-list`, which is the app shell
 * booting — that route is commented out in the legacy router, so the sweep
 * never reached a user screen at all.
 *
 * Traps this encodes:
 *
 * - The list body is `{ pageno, pagesize, query, add_filter }`. Note
 *   `add_filter` SINGULAR — every other list in this API says `addfilters` —
 *   and `query`, a bare string the backend interprets over fields it does not
 *   name. There is no `filter_key` to copy.
 *
 * - STATUS HAS TWO VOCABULARIES. A row reads `'active'` / `'inactive'`;
 *   `change_status` writes `'enabled'` / `'disabled'`. Sending back what was
 *   read is not a no-op, it is an unrecognised value.
 *
 * - `last_login` is epoch SECONDS, and 0 means never.
 *
 * - The service is internally inconsistent about parameter names: delete reads
 *   `user_id`, edit reads `userId`. Reproduced rather than unified, because
 *   unifying breaks whichever one is right.
 */

export const platformUser = z
  .object({
    /** Stable identity for every per-user action. */
    userId: z.string(),
    name: z.string().default(''),
    emailId: z.string().default(''),
    /**
     * 'Root' | 'OrgAdmin' | 'User' | 'ContentManager', per type_list. Same
     * "null"-as-a-string hazard as `phone`; the legacy sorter guards it too.
     */
    userType: z
      .string()
      .default('')
      .transform((v) => (v === 'null' ? '' : v)),
    /** 'active' | 'inactive' — NOT the vocabulary change_status takes. */
    status: z.string().default(''),
    /**
     * The wire sends the STRING "null" for a missing number — the legacy
     * sorter guards `a.phone === 'null'` explicitly. Passed through it renders
     * as the literal text "null" in the table.
     */
    phone: z
      .string()
      .nullable()
      .default(null)
      .transform((v) => (v === 'null' || v === '' ? null : v)),
    /** The org's NAME. Whether a row also carries its id is unverified — G10. */
    orgName: z.string().default(''),
    /** Number of tenants the user reaches. Capital T on the wire. */
    TenantCount: z.number().default(0),
    /** Epoch SECONDS; 0 or null means never signed in. */
    last_login: z.number().nullable().default(null),
  })
  .passthrough()

export type PlatformUser = z.output<typeof platformUser>

export const platformUsersResponse = flatCountEnvelope(platformUser)

/** What a row reads. */
export const USER_ACTIVE = 'active'
export const USER_INACTIVE = 'inactive'
/** What change_status writes. Deliberately different constants. */
export const USER_ENABLE = 'enabled'
export const USER_DISABLE = 'disabled'

/** The userType vocabulary, served by type_list. */
export const userTypesResponse = z.object({
  status: z.string().optional(),
  message: z.string().optional(),
  data: z.array(z.string()).default([]),
})

export interface PlatformUsersPage {
  users: PlatformUser[]
  /** Total across all pages. */
  total: number
}

export interface PlatformUsersQuery {
  /** 1-based. */
  pageNo?: number
  pageSize?: number
  /** A bare string. What the backend matches it against is not documented. */
  query?: string
  /** The `add_filter` list. Shape unverified beyond it being an array. */
  filters?: unknown[]
}

export interface UserStatusInput {
  userId: string
  /** USER_ENABLE or USER_DISABLE — never a value read off a row. */
  status: string
  /** The legacy screen sends it; the path does not carry it. */
  orgId?: string
}

export interface UserResetInput {
  userId: string
  orgId?: string
}

export interface PlatformUsersApi {
  list: (q: PlatformUsersQuery) => Promise<PlatformUsersPage>
  /** The userType vocabulary. Unlike token capabilities, this IS served. */
  userTypes: () => Promise<string[]>
  setStatus: (input: UserStatusInput) => Promise<void>
  resetPassword: (input: UserResetInput) => Promise<void>
  resetMfa: (input: UserResetInput) => Promise<void>
  /** One user, with the access tree. Returns null when the id is unknown. */
  detail: (userId: string) => Promise<UserDetail | null>
  create: (input: CreateUserInput) => Promise<void>
  update: (input: UpdateUserInput) => Promise<void>
  remove: (input: DeleteUserInput) => Promise<void>
}

const ack = ackEnvelope

export function createPlatformUsersApi(client: ApiClient): PlatformUsersApi {
  return {
    list: async ({ pageNo = 1, pageSize = 50, query = '', filters = [] } = {}) => {
      const res = await client.request('POST', '/lc/mgmt/org/user-list', {
        body: {
          pageno: pageNo,
          pagesize: pageSize,
          query,
          // `add_filter`, singular. Sent even when empty, because that is what
          // the legacy screen does here — unlike the plugin and token lists,
          // which omit the key.
          add_filter: filters,
        },
        schema: platformUsersResponse,
      })
      return { users: res.data, total: res.count }
    },

    userTypes: async () => {
      const res = await client.request('GET', '/lc/mgmt/type_list?type=userTypes', {
        schema: userTypesResponse,
      })
      return res.data
    },

    setStatus: async ({ userId, status, orgId }) => {
      const res = await client.request('POST', `/lc/mgmt/org/user/${encodeURIComponent(userId)}/change_status`, {
        body: { status, ...(orgId ? { orgId } : {}) },
        schema: ack,
      })
      assertAck(res, 'Changing the status')
    },

    resetPassword: async ({ userId, orgId }) => {
      const res = await client.request('PUT', `/lc/mgmt/users/reset_pass/${encodeURIComponent(userId)}`, {
        body: { userId, ...(orgId ? { orgId } : {}) },
        schema: ack,
      })
      assertAck(res, 'The password reset')
    },

    resetMfa: async ({ userId, orgId }) => {
      const res = await client.request('PUT', `/lc/mgmt/users/reset_mfa/${encodeURIComponent(userId)}`, {
        body: { userId, ...(orgId ? { orgId } : {}) },
        schema: ack,
      })
      assertAck(res, 'The MFA reset')
    },

    detail: async (userId) =>
      client.request('GET', `/lc/mgmt/users/${encodeURIComponent(userId)}`, {
        schema: userDetailResponse,
      }),

    create: async ({ orgId, name, emailId, phone, userType }) => {
      const res = await client.request('POST', `/lc/mgmt/org/${encodeURIComponent(orgId)}/user`, {
        body: { name: name.trim(), emailId: emailId.trim(), phone, userType },
        schema: ack,
      })
      assertAck(res, 'Creating the user')
    },

    update: async (input) => {
      // PUT /lc/mgmt/users/{userId} — this path takes NO orgId, which is why
      // editing works from a flat list while create and delete do not.
      const res = await client.request('PUT', `/lc/mgmt/users/${encodeURIComponent(input.userId)}`, {
        body: {
          userId: input.userId,
          name: input.name.trim(),
          emailId: input.emailId.trim(),
          phone: input.phone,
          orgName: input.orgName,
          status: input.status,
          isAdmin: input.isAdmin,
          mfa_flag: input.mfa_flag,
          access: input.access,
        },
        schema: ack,
      })
      assertAck(res, 'Updating the user')
    },

    remove: async ({ userId, orgId }) => {
      const res = await client.request(
        'DELETE',
        `/lc/mgmt/org/${encodeURIComponent(orgId)}/user/${encodeURIComponent(userId)}`,
        { schema: ack },
      )
      assertAck(res, 'Deleting the user')
    },
  }
}

/* ------------------------------------------------------------------ display */

export function isUserActive(u: PlatformUser): boolean {
  return u.status === USER_ACTIVE
}

/**
 * The status to WRITE to flip this user.
 *
 * Exists so a caller can never reach for `u.status` — that is the read
 * vocabulary, and posting it back sends a value change_status does not know.
 */
export function nextUserStatus(u: PlatformUser): string {
  return isUserActive(u) ? USER_DISABLE : USER_ENABLE
}

/**
 * Whether the enable/disable toggle can be moved.
 *
 * Asymmetric, as in the legacy screen: DISABLING is always allowed, ENABLING
 * additionally required the user's organization to be enabled. The flat list
 * row carries `orgName` but not the org's state, so the precondition cannot be
 * evaluated here (G11). `orgEnabled` is threaded through for a caller that
 * does know; left undefined the enable is ATTEMPTED and the server's refusal
 * is surfaced, rather than the control being disabled on a guess.
 */
export function canToggleUser(u: PlatformUser, orgEnabled?: boolean): boolean {
  if (isUserActive(u)) return true
  return orgEnabled !== false
}

/** Epoch SECONDS to a Date. 0 means never signed in, and is not a date. */
export function lastLoginDate(u: PlatformUser): Date | null {
  const t = u.last_login
  return typeof t === 'number' && t > 0 ? new Date(t * 1000) : null
}

/**
 * One user, from `GET /lc/mgmt/users/{userId}`.
 *
 * The payload is an ARRAY of one — `response.data.data[0]` in the legacy
 * screen — not an object. It does NOT carry `orgId`; the legacy flat page
 * reads `orgName` from it and nothing anywhere reads an id off a user.
 */
export const userAccessScope = z
  .object({
    scope_id: z.string(),
    scope_name: z.string().default(''),
    role: z.string().default(''),
  })
  .passthrough()

export const userAccessCluster = z
  .object({
    clusterId: z.string(),
    access_type: z.string().default(''),
    scope_access: z.array(userAccessScope).default([]),
  })
  .passthrough()

export const userDetail = z
  .object({
    userId: z.string(),
    name: z.string().default(''),
    emailId: z.string().default(''),
    phone: z.string().nullable().default(null),
    orgName: z.string().default(''),
    status: z.string().default(''),
    isAdmin: z.boolean().default(false),
    mfa_flag: z.boolean().default(false),
    access: z.array(userAccessCluster).default([]),
  })
  .passthrough()

export type UserAccessScope = z.output<typeof userAccessScope>
export type UserAccessCluster = z.output<typeof userAccessCluster>
export type UserDetail = z.output<typeof userDetail>

/** `data` is an array of ONE. Unwrapped here so no call site repeats `[0]`. */
export const userDetailResponse = z
  .object({
    status: z.string().optional(),
    message: z.string().optional(),
    data: z.array(userDetail).default([]),
  })
  .transform((r) => r.data[0] ?? null)

export interface CreateUserInput {
  /** From the org PICKER, never inferred from a name. */
  orgId: string
  name: string
  emailId: string
  phone: string | null
  userType: string
}

export interface UpdateUserInput {
  userId: string
  name: string
  emailId: string
  phone: string | null
  /** The org's NAME — this endpoint has never taken an id. */
  orgName: string
  status: string
  isAdmin: boolean
  mfa_flag: boolean
  access: UserAccessCluster[]
}

export interface DeleteUserInput {
  userId: string
  /** Required, and in the PATH. Must come from a resolved organization. */
  orgId: string
}

/* ------------------------------------------------------------ access tree */

/** One cluster in the access editor, merged from the deployment and the user. */
export interface AccessRow {
  clusterId: string
  clusterName: string
  /** '' means no access at the cluster level. */
  accessType: string
  scopes: Array<{ scopeId: string; scopeName: string; role: string }>
}

/**
 * Merge the clusters this deployment has with the access this user holds.
 *
 * Driven by the DEPLOYMENT, not by the user: a cluster the user has no access
 * to must still appear, or it cannot be granted. A cluster in the user's
 * access that no longer exists is dropped — showing it would offer a grant
 * that cannot be saved.
 */
export function buildAccessRows(
  deployment: ReadonlyArray<{ clusterId: string; clusterName: string; scopes: ReadonlyArray<{ scopeId: string; scopeName: string }> }>,
  access: readonly UserAccessCluster[],
): AccessRow[] {
  const held = new Map(access.map((a) => [a.clusterId, a]))
  return deployment.map((c) => {
    const mine = held.get(c.clusterId)
    const roles = new Map((mine?.scope_access ?? []).map((s) => [s.scope_id, s.role]))
    return {
      clusterId: c.clusterId,
      clusterName: c.clusterName,
      accessType: mine?.access_type ?? '',
      scopes: c.scopes.map((s) => ({
        scopeId: s.scopeId,
        scopeName: s.scopeName,
        role: roles.get(s.scopeId) ?? '',
      })),
    }
  })
}

/**
 * Back to the wire shape.
 *
 * A cluster with no access type AND no scope roles is omitted entirely rather
 * than sent as an empty grant — the legacy payload only ever carried clusters
 * the user actually reaches, and an empty entry is an untested shape.
 */
export function accessRowsToPayload(rows: readonly AccessRow[]): UserAccessCluster[] {
  const out: UserAccessCluster[] = []
  for (const r of rows) {
    const scope_access = r.scopes
      .filter((s) => s.role)
      .map((s) => ({ scope_id: s.scopeId, scope_name: s.scopeName, role: s.role }))
    if (!r.accessType && !scope_access.length) continue
    out.push({ clusterId: r.clusterId, access_type: r.accessType, scope_access })
  }
  return out
}
